import { X, MapPin, Factory, ExternalLink } from 'lucide-react'
import type { GammaIrradiatorFacility } from '@/lib/api'

interface Props {
  facility: GammaIrradiatorFacility
  onClose: () => void
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-start gap-1.5">
      <span className="text-gray-600 mt-0.5">{icon}</span>
      <div className="text-[10px]">
        <span className="text-gray-600">{label}: </span>
        <span className="text-gray-400">{value}</span>
      </div>
    </div>
  )
}

export default function GammaDetailCard({ facility, onClose }: Props) {
  const { lat, lon, city, country } = facility
  const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lon}&z=16`
  const osmUrl = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}&zoom=16`

  return (
    <div className="bg-gray-950/95 backdrop-blur border border-yellow-900/50 rounded overflow-hidden shadow-[0_0_15px_rgba(234,179,8,0.12)]">
      <div className="flex items-start justify-between px-4 py-3 border-b border-yellow-900/40 bg-gray-900/50">
        <div className="flex-1 min-w-0 pr-2">
          <h3 className="text-sm font-semibold text-gray-100 leading-snug flex items-center gap-1.5">
            <span className="text-yellow-400">☢</span>
            Gamma irradiator
          </h3>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] font-mono text-gray-500">
            <Factory className="w-3.5 h-3.5 text-yellow-500" />
            <span>{[city, country].filter(Boolean).join(', ') || '—'}</span>
          </div>
        </div>
        <button onClick={onClose} className="text-gray-600 hover:text-gray-300 transition-colors p-0.5">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-3 space-y-3 text-xs font-mono">
        <div className="space-y-1.5">
          <InfoRow
            icon={<MapPin className="w-3 h-3" />}
            label="Position"
            value={`${lat.toFixed(4)}, ${lon.toFixed(4)}`}
          />
        </div>
        <div className="flex flex-wrap gap-2 pt-1 border-t border-yellow-900/30">
          <a
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-[10px] bg-gray-800/80 border border-yellow-900/50 text-yellow-200 hover:bg-yellow-500/10 hover:border-yellow-500/50 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            Google Maps
          </a>
          <a
            href={osmUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-[10px] bg-gray-800/80 border border-yellow-900/50 text-yellow-200 hover:bg-yellow-500/10 hover:border-yellow-500/50 transition-colors"
          >
            <ExternalLink className="w-3 h-3" />
            OpenStreetMap
          </a>
        </div>
      </div>
    </div>
  )
}
